import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'isomorphic-dompurify';

function PostDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null); // 답글 대상
  const [editingComment, setEditingComment] = useState(null); // 수정 중인 댓글
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // URL 패턴에 따라 boardId와 postId 추출
  // /board/:boardId/:postId -> boardId, postId
  // /theo/:section/:postId -> section (boardId로 사용), postId
  // /bible/:section/:postId -> section (boardId로 사용), postId
  // /columns/:columnist/:postId -> columnist (boardId로 사용), postId
  const boardId = params.boardId || params.section || params.columnist;
  const postId = params.postId;

  const boardNames = {
    'notice': '알림판',
    'bible_index': '성서마당',
    'theo_index': '신학마당',
    'current': '세상읽기',
    'free': '사랑채',
    'sermon': '설교모음',
    'mark': '매일묵상',
    'churchs': '교회소식',
    'member': '후원안내',
    // 섹션별 이름
    'bible': '성서연구',
    'comment': '설교비평',
    'berg': '판넨베르크',
    'study1': '특강',
    'study2': '온라인 강의실',
    // 칼럼니스트
    'jung': '정용섭 칼럼',
    'kim': '김기석 칼럼',
    'lee': '이정배 칼럼'
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // If YYYYMMDD... format
    if (dateStr.length >= 8) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      // Optional time
      const hour = dateStr.length >= 10 ? dateStr.substring(8, 10) : '00';
      const minute = dateStr.length >= 12 ? dateStr.substring(10, 12) : '00';
      return `${year}.${month}.${day} ${hour}:${minute}`;
    }
    return dateStr;
  };

  // Content Image Processor
  const processContent = (html) => {
    if (!html) return '';

    // 1. Normalize paths to http://files.dabia.net/
    // files.dabia.net needs to be accessed via proxy due to SSL/Mixed Content issues
    let processed = html.replace(/src="\/files\//g, 'src="http://files.dabia.net/');
    processed = processed.replace(/src="files\//g, 'src="http://files.dabia.net/');
    processed = processed.replace(/src="http:\/\/dabia\.net\/files\//g, 'src="http://files.dabia.net/');
    processed = processed.replace(/src="https:\/\/dabia\.net\/files\//g, 'src="http://files.dabia.net/');
    processed = processed.replace(/src="\/xe\/files\//g, 'src="http://files.dabia.net/');
    processed = processed.replace(/src="http:\/\/dabia\.net\/xe\/files\//g, 'src="http://files.dabia.net/');
    processed = processed.replace(/src="https:\/\/dabia\.net\/xe\/files\//g, 'src="http://files.dabia.net/');

    // 2. Wrap with Proxy API
    const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

    processed = processed.replace(/src="(http:\/\/files\.dabia\.net\/[^"]+)"/g, (match, url) => {
      return `src="${apiBase}/api/proxy-image?url=${encodeURIComponent(url)}"`;
    });

    return processed;
  };

  // 사용자 정보 로드
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser(payload);
      } catch (e) {
        console.error('Token parsing error:', e);
      }
    }
  }, []);

  // 게시글 불러오기
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
        const response = await fetch(`${apiBase}/api/post?id=${postId}`);
        const data = await response.json();

        if (data && !data.error) {
          setPost({
            title: data.title,
            content: processContent(data.content),
            author: data.author || data.nick_name || data.user_name || '관리자', // Fallbacks
            date: data.date,
            views: data.readed_count || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId]);

  // 댓글 불러오기
  useEffect(() => {
    if (!postId) return;

    const fetchComments = async () => {
      try {
        const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
        const response = await fetch(`${apiBase}/api/comments?document_srl=${postId}`);
        const data = await response.json();

        if (data && Array.isArray(data)) {
          setComments(data);
        } else {
          setComments([]);
        }
      } catch (error) {
        console.error('Failed to fetch comments:', error);
        setComments([]);
      }
    };

    fetchComments();
  }, [postId]);

  // 댓글 새로고침 함수
  const refreshComments = async () => {
    try {
      const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
      const response = await fetch(`${apiBase}/api/comments?document_srl=${postId}`);
      const data = await response.json();
      if (data && Array.isArray(data)) {
        setComments(data);
      }
    } catch (error) {
      console.error('Failed to refresh comments:', error);
    }
  };

  // 댓글/대댓글 작성
  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }

    setCommentLoading(true);

    try {
      const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
      const response = await fetch(`${apiBase}/api/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          document_srl: postId,
          content: newComment,
          parent_srl: replyTo?.comment_srl || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await refreshComments();
        setNewComment('');
        setReplyTo(null);
        alert(replyTo ? '답글이 작성되었습니다.' : '댓글이 작성되었습니다.');
      } else {
        alert(data.error || '댓글 작성에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to submit comment:', error);
      alert('댓글 작성 중 오류가 발생했습니다.');
    } finally {
      setCommentLoading(false);
    }
  };

  // 댓글 수정
  const handleCommentEdit = async () => {
    if (!editContent.trim()) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
      const response = await fetch(`${apiBase}/api/comments/${editingComment.comment_srl}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: editContent })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await refreshComments();
        setEditingComment(null);
        setEditContent('');
        alert('댓글이 수정되었습니다.');
      } else {
        alert(data.error || '댓글 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to edit comment:', error);
      alert('댓글 수정 중 오류가 발생했습니다.');
    }
  };

  // 댓글 삭제
  const handleCommentDelete = async (commentSrl) => {
    if (!window.confirm('정말 이 댓글을 삭제하시겠습니까?')) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
      const response = await fetch(`${apiBase}/api/comments/${commentSrl}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await refreshComments();
        alert('댓글이 삭제되었습니다.');
      } else {
        alert(data.error || '댓글 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
      alert('댓글 삭제 중 오류가 발생했습니다.');
    }
  };

  // 대댓글 렌더링을 위한 계층 구조 구성
  const buildCommentTree = (comments) => {
    const commentMap = {};
    const rootComments = [];

    // 모든 댓글을 맵에 저장
    comments.forEach(comment => {
      commentMap[comment.comment_srl] = { ...comment, replies: [] };
    });

    // 부모-자식 관계 설정
    comments.forEach(comment => {
      if (comment.parent_srl && commentMap[comment.parent_srl]) {
        commentMap[comment.parent_srl].replies.push(commentMap[comment.comment_srl]);
      } else {
        rootComments.push(commentMap[comment.comment_srl]);
      }
    });

    return rootComments;
  };

  if (loading) {
    return (
      <div id="contentBody" style={{ display: 'block', padding: '30px 40px' }}>
        <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', textAlign: 'center' }}>
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  // 목록으로 돌아가기 함수
  const getBackUrl = () => {
    if (params.section) {
      // /theo/:section/:postId 또는 /bible/:section/:postId
      const prefix = window.location.pathname.includes('/theo/') ? 'theo' : 'bible';
      return `/${prefix}/${params.section}`;
    } else if (params.columnist) {
      // /columns/:columnist/:postId
      return `/columns/${params.columnist}`;
    } else {
      // /board/:boardId/:postId
      return `/board/${boardId}`;
    }
  };

  if (!post) {
    return (
      <div id="contentBody" style={{ display: 'block', padding: '30px 40px' }}>
        <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', textAlign: 'center' }}>
          <p>게시글을 찾을 수 없습니다.</p>
          <button onClick={() => navigate(getBackUrl())} style={{
            marginTop: '20px',
            padding: '10px 20px',
            background: '#748320',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}>
            목록으로
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id="contentBody" style={{ display: 'block', padding: '30px 40px' }}>
      <div style={{ background: '#fff', borderRadius: '10px', padding: '30px' }}>
        {/* 게시판 제목 */}
        <div style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #748320' }}>
          <h2 style={{ fontSize: '20px', color: '#333', margin: 0 }}>{boardNames[boardId] || boardId}</h2>
        </div>

        {/* 게시글 제목 */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#333', lineHeight: '1.4', margin: 0 }}>
            {post.title}
          </h1>
        </div>

        {/* 게시글 정보 */}
        <div style={{
          display: 'flex',
          gap: '20px',
          padding: '15px',
          background: '#f8f8f8',
          borderRadius: '6px',
          marginBottom: '30px',
          fontSize: '14px',
          color: '#666'
        }}>
          <div>
            <strong>작성자:</strong> {post.author}
          </div>
          <div>
            <strong>작성일:</strong> {formatDate(post.date)}
          </div>
          <div>
            <strong>조회:</strong> {post.views}
          </div>
        </div>

        {/* 게시글 내용 */}
        <div style={{
          minHeight: '300px',
          padding: '20px 0',
          lineHeight: '1.8',
          fontSize: '15px',
          color: '#333',
          borderBottom: '1px solid #e8e8e8',
          marginBottom: '30px'
        }}>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />
        </div>

        {/* 댓글 영역 */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
            댓글 ({comments.length})
          </h3>

          {/* 댓글 목록 */}
          <div style={{ marginBottom: '20px' }}>
            {comments.length === 0 ? (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#999',
                background: '#f8f8f8',
                borderRadius: '6px'
              }}>
                첫 댓글을 작성해보세요.
              </div>
            ) : (
              buildCommentTree(comments).map((comment) => (
                <CommentItem
                  key={comment.comment_srl}
                  comment={comment}
                  depth={0}
                  currentUser={currentUser}
                  editingComment={editingComment}
                  editContent={editContent}
                  setEditContent={setEditContent}
                  setEditingComment={setEditingComment}
                  setReplyTo={setReplyTo}
                  handleCommentEdit={handleCommentEdit}
                  handleCommentDelete={handleCommentDelete}
                  formatDate={formatDate}
                />
              ))
            )}
          </div>

          {/* 댓글 작성 폼 */}
          <form onSubmit={handleCommentSubmit}>
            {replyTo && (
              <div style={{
                padding: '10px',
                background: '#e3f2fd',
                borderRadius: '4px',
                marginBottom: '10px',
                fontSize: '13px',
                color: '#1976d2',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>@{replyTo.author} 님에게 답글 작성 중</span>
                <button
                  type="button"
                  onClick={() => setReplyTo(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1976d2',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  취소
                </button>
              </div>
            )}
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={replyTo ? "답글을 입력하세요..." : "댓글을 입력하세요..."}
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '15px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical',
                marginBottom: '10px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ textAlign: 'right' }}>
              <button
                type="submit"
                disabled={commentLoading}
                style={{
                  padding: '10px 20px',
                  background: commentLoading ? '#ccc' : '#748320',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: commentLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {commentLoading ? '작성 중...' : (replyTo ? '답글 작성' : '댓글 작성')}
              </button>
            </div>
          </form>
        </div>

        {/* 버튼 영역 */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={() => navigate(getBackUrl())}
            style={{
              padding: '10px 20px',
              background: '#748320',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#5e6a1a'}
            onMouseOut={(e) => e.target.style.background = '#748320'}
          >
            목록으로
          </button>
        </div>
      </div>
    </div>
  );
}

// CommentItem component for recursive rendering
function CommentItem({ comment, depth, currentUser, editingComment, editContent, setEditContent, setEditingComment, setReplyTo, handleCommentEdit, handleCommentDelete, formatDate }) {
  const isOwner = currentUser && currentUser.member_srl === comment.member_srl;
  const isEditing = editingComment?.comment_srl === comment.comment_srl;

  return (
    <>
      <div style={{
        marginLeft: `${depth * 40}px`,
        padding: '15px',
        background: depth % 2 === 0 ? '#f8f8f8' : '#fff',
        border: '1px solid #e8e8e8',
        borderRadius: '6px',
        marginBottom: '10px'
      }}>
        {/* Comment header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '10px',
          borderBottom: '1px solid #e8e8e8'
        }}>
          <div style={{ display: 'flex', gap: '10px', fontSize: '13px', color: '#666' }}>
            <strong style={{ color: '#333' }}>{comment.author}</strong>
            <span>{formatDate(comment.regdate)}</span>
          </div>

          {/* Action buttons */}
          {!isEditing && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setReplyTo(comment)}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  background: '#e3f2fd',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                답글
              </button>
              {isOwner && (
                <>
                  <button
                    onClick={() => {
                      setEditingComment(comment);
                      setEditContent(comment.content);
                    }}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      background: '#fff3e0',
                      color: '#f57c00',
                      border: '1px solid #f57c00',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleCommentDelete(comment.comment_srl)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      background: '#ffebee',
                      color: '#d32f2f',
                      border: '1px solid #d32f2f',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    삭제
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Comment content or edit mode */}
        {isEditing ? (
          <div>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical',
                marginBottom: '10px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCommentEdit}
                style={{
                  padding: '6px 16px',
                  background: '#748320',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                저장
              </button>
              <button
                onClick={() => {
                  setEditingComment(null);
                  setEditContent('');
                }}
                style={{
                  padding: '6px 16px',
                  background: '#999',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                취소
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            fontSize: '14px',
            color: '#333',
            lineHeight: '1.6'
          }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment.content) }} />
        )}
      </div>

      {/* Recursive rendering of replies */}
      {comment.replies && comment.replies.length > 0 && comment.replies.map((reply) => (
        <CommentItem
          key={reply.comment_srl}
          comment={reply}
          depth={depth + 1}
          currentUser={currentUser}
          editingComment={editingComment}
          editContent={editContent}
          setEditContent={setEditContent}
          setEditingComment={setEditingComment}
          setReplyTo={setReplyTo}
          handleCommentEdit={handleCommentEdit}
          handleCommentDelete={handleCommentDelete}
          formatDate={formatDate}
        />
      ))}
    </>
  );
}

export default PostDetail;
